import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Profile.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'http://localhost:5000/api';

const buildEditableProfile = (profile = {}) => ({
  name: profile.name || '',
  company: profile.company || '',
  phone: profile.phone || '',
  position: profile.position || '',
  website: profile.website || '',
  location: profile.location || '',
  bio:
    profile.bio ||
    profile.description ||
    ''
});

const formatDate = (value) => {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatNumber = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return new Intl.NumberFormat(
    'en-US'
  ).format(number);
};

const getWebsiteHref = (website) => {
  if (!website) {
    return '';
  }

  if (
    website.startsWith('https://') ||
    website.startsWith('http://')
  ) {
    return website;
  }

  return `https://${website}`;
};

const Profile = () => {
  const navigate = useNavigate();

  const {
    user,
    updateProfile,
    changePassword
  } = useAuth();

  const avatarInputRef = useRef(null);

  const [profile, setProfile] =
    useState(null);

  const [vessels, setVessels] =
    useState([]);

  const [rfqs, setRfqs] =
    useState([]);

  const [formData, setFormData] =
    useState(buildEditableProfile());

  const [passwordData, setPasswordData] =
    useState({
      current: '',
      new: '',
      confirm: ''
    });

  const [loading, setLoading] =
    useState(true);

  const [isEditing, setIsEditing] =
    useState(false);

  const [showSecurity, setShowSecurity] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [
    changingPassword,
    setChangingPassword
  ] = useState(false);

  const [
    avatarUploading,
    setAvatarUploading
  ] = useState(false);

  const [message, setMessage] =
    useState('');

  const [messageType, setMessageType] =
    useState('');

  const [
    avatarTimestamp,
    setAvatarTimestamp
  ] = useState(Date.now());

  const [avatarFailed, setAvatarFailed] =
    useState(false);

  const getToken = () =>
    localStorage.getItem(
      'marilink_token'
    );

  const showMessage = (
    text,
    type = 'success'
  ) => {
    setMessage(text);
    setMessageType(type);

    window.setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const fetchProfile = async (
    showLoader = true
  ) => {
    try {
      if (showLoader) {
        setLoading(true);
      }

      const token = getToken();

      const response = await fetch(
        `${API_BASE}/users/me`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`
          }
        }
      );

      const data =
        await response.json();

      if (
        !response.ok ||
        !data.success
      ) {
        throw new Error(
          data.message ||
            'Unable to load profile.'
        );
      }

      setProfile(data.user);

      setFormData(
        buildEditableProfile(
          data.user
        )
      );

      setAvatarTimestamp(
        Date.now()
      );

      setAvatarFailed(false);

      if (
        data.user.role === 'owner'
      ) {
        await loadOwnerData(token);
      }
    } catch (error) {
      console.error(
        'Profile loading error:',
        error
      );

      showMessage(
        error.message ||
          'Unable to load profile.',
        'error'
      );
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const loadOwnerData = async (
    token
  ) => {
    const [
      vesselsResult,
      rfqsResult
    ] = await Promise.allSettled([
      fetch(`${API_BASE}/vessels`, {
        headers: {
          Authorization:
            `Bearer ${token}`
        }
      }).then((response) =>
        response.json()
      ),

      fetch(`${API_BASE}/rfqs`, {
        headers: {
          Authorization:
            `Bearer ${token}`
        }
      }).then((response) =>
        response.json()
      )
    ]);

    if (
      vesselsResult.status ===
        'fulfilled' &&
      vesselsResult.value?.success
    ) {
      setVessels(
        vesselsResult.value.vessels ||
          []
      );
    } else {
      setVessels([]);
    }

    if (
      rfqsResult.status ===
        'fulfilled' &&
      rfqsResult.value?.success
    ) {
      setRfqs(
        rfqsResult.value.rfqs || []
      );
    } else {
      setRfqs([]);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const isOwner =
    profile?.role === 'owner';

  const activeVessels =
    useMemo(
      () =>
        vessels.filter(
          (vessel) =>
            vessel.status === 'active'
        ),
      [vessels]
    );

  const maintenanceVessels =
    useMemo(
      () =>
        vessels.filter(
          (vessel) =>
            vessel.status ===
            'maintenance'
        ),
      [vessels]
    );

  const openRfqs =
    useMemo(
      () =>
        rfqs.filter(
          (rfq) =>
            rfq.status === 'open'
        ),
      [rfqs]
    );

  const vesselTypes =
    useMemo(() => {
      return [
        ...new Set(
          vessels
            .map(
              (vessel) =>
                vessel.type
            )
            .filter(Boolean)
        )
      ];
    }, [vessels]);

  const flags =
    useMemo(() => {
      return [
        ...new Set(
          vessels
            .map(
              (vessel) =>
                vessel.flag
            )
            .filter(Boolean)
        )
      ];
    }, [vessels]);

  const totalGRT =
    useMemo(() => {
      return vessels.reduce(
        (total, vessel) =>
          total +
          (Number(vessel.grt) ||
            0),
        0
      );
    }, [vessels]);

  const profileCompleteness =
    useMemo(() => {
      if (!profile) {
        return 0;
      }

      const fields = [
        profile.name,
        profile.company,
        profile.email,
        profile.phone,
        profile.position,
        profile.website,
        profile.location,
        profile.bio ||
          profile.description
      ];

      const completed =
        fields.filter(
          (value) =>
            value !== undefined &&
            value !== null &&
            String(value).trim()
        ).length;

      return Math.round(
        (completed /
          fields.length) *
          100
      );
    }, [profile]);

  const recentVessels =
    useMemo(() => {
      return [...vessels]
        .sort(
          (a, b) =>
            new Date(
              b.createdAt || 0
            ).getTime() -
            new Date(
              a.createdAt || 0
            ).getTime()
        )
        .slice(0, 4);
    }, [vessels]);

  const handleEdit = () => {
    setFormData(
      buildEditableProfile(
        profile
      )
    );

    setIsEditing(true);
  };

  const cancelEdit = () => {
    setFormData(
      buildEditableProfile(
        profile
      )
    );

    setIsEditing(false);
  };

  const handleInputChange = (
    event
  ) => {
    const {
      name,
      value
    } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (
      !formData.name.trim()
    ) {
      showMessage(
        'Full name is required.',
        'error'
      );
      return;
    }

    if (
      isOwner &&
      !formData.company.trim()
    ) {
      showMessage(
        'Company name is required.',
        'error'
      );
      return;
    }

    try {
      setSaving(true);

      const result =
        await updateProfile({
          name:
            formData.name.trim(),
          company:
            formData.company.trim(),
          phone:
            formData.phone.trim(),
          position:
            formData.position.trim(),
          website:
            formData.website.trim(),
          location:
            formData.location.trim(),
          bio:
            formData.bio.trim()
        });

      if (!result.success) {
        throw new Error(
          result.message ||
            result.error ||
            'Profile update failed.'
        );
      }

      setProfile(
        result.user
      );

      setFormData(
        buildEditableProfile(
          result.user
        )
      );

      setIsEditing(false);

      showMessage(
        'Profile updated successfully.',
        'success'
      );
    } catch (error) {
      showMessage(
        error.message ||
          'Unable to update profile.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarClick = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (
    event
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif'
    ];

    if (
      !allowedTypes.includes(
        file.type
      )
    ) {
      showMessage(
        'Please select a JPEG, PNG, WEBP or GIF image.',
        'error'
      );

      event.target.value = '';
      return;
    }

    if (
      file.size >
      15 * 1024 * 1024
    ) {
      showMessage(
        'Profile image must be smaller than 15 MB.',
        'error'
      );

      event.target.value = '';
      return;
    }

    try {
      setAvatarUploading(true);

      const body =
        new FormData();

      body.append(
        'avatarFile',
        file
      );

      const response = await fetch(
        `${API_BASE}/users/complete-profile`,
        {
          method: 'PUT',
          headers: {
            Authorization:
              `Bearer ${getToken()}`
          },
          body
        }
      );

      const data =
        await response.json();

      if (
        !response.ok ||
        !data.success
      ) {
        throw new Error(
          data.message ||
            'Unable to upload profile picture.'
        );
      }

      setAvatarTimestamp(
        Date.now()
      );

      setAvatarFailed(false);

      await fetchProfile(false);

      showMessage(
        'Profile picture updated.',
        'success'
      );
    } catch (error) {
      showMessage(
        error.message ||
          'Profile picture update failed.',
        'error'
      );
    } finally {
      setAvatarUploading(false);

      if (
        avatarInputRef.current
      ) {
        avatarInputRef.current.value =
          '';
      }
    }
  };

  const handlePasswordChange =
    async (event) => {
      event.preventDefault();

      if (
        passwordData.new !==
        passwordData.confirm
      ) {
        showMessage(
          'New passwords do not match.',
          'error'
        );
        return;
      }

      if (
        passwordData.new.length <
        6
      ) {
        showMessage(
          'New password must contain at least 6 characters.',
          'error'
        );
        return;
      }

      try {
        setChangingPassword(
          true
        );

        const result =
          await changePassword(
            passwordData.current,
            passwordData.new
          );

        if (!result.success) {
          throw new Error(
            result.message ||
              result.error ||
              'Password update failed.'
          );
        }

        setPasswordData({
          current: '',
          new: '',
          confirm: ''
        });

        setShowSecurity(false);

        showMessage(
          'Password changed successfully.',
          'success'
        );
      } catch (error) {
        showMessage(
          error.message ||
            'Unable to change password.',
          'error'
        );
      } finally {
        setChangingPassword(
          false
        );
      }
    };

  if (loading) {
    return (
      <div className="owner-profile-state">
        <span className="owner-profile-loader"></span>
        Loading profile...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="owner-profile-state">
        <i className="fas fa-exclamation-circle"></i>
        Profile information is unavailable.
      </div>
    );
  }

  const userId =
    profile._id ||
    profile.id ||
    user?._id ||
    user?.id;

  const avatarUrl =
    userId
      ? `${API_BASE}/avatar/${userId}?t=${avatarTimestamp}`
      : '';

  const accountStatus =
    profile.isActive === false
      ? 'Inactive'
      : 'Active';

  const verificationText =
    profile.isVerified
      ? 'Verified'
      : profile.verificationStatus
        ? profile.verificationStatus
            .charAt(0)
            .toUpperCase() +
          profile.verificationStatus.slice(
            1
          )
        : 'Pending';

  const authMethod =
    profile.authProvider ===
      'google' ||
    profile.isGoogleLogin
      ? 'Google'
      : 'Email & Password';

  return (
    <div className="owner-profile-page">
      <input
        ref={avatarInputRef}
        type="file"
        className="owner-profile-file-input"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={
          handleAvatarChange
        }
      />

      <div className="owner-profile-page-header">
        <div>
          <h1>
            {isOwner
              ? 'Ship Owner Profile'
              : 'My Profile'}
          </h1>

          <p>
            Manage your account,
            company and fleet
            information
          </p>
        </div>

        <div className="owner-profile-header-actions">
          {isOwner && (
            <>
              <button
                type="button"
                className="owner-profile-secondary-btn"
                onClick={() =>
                  navigate(
                    '/owner/my-ships'
                  )
                }
              >
                <i className="fas fa-ship"></i>
                My Fleet
              </button>

              <button
                type="button"
                className="owner-profile-secondary-btn"
                onClick={() =>
                  navigate(
                    '/owner/post-job'
                  )
                }
              >
                <i className="fas fa-plus"></i>
                Post Job
              </button>
            </>
          )}

          {!isEditing && (
            <button
              type="button"
              className="owner-profile-edit-btn"
              onClick={
                handleEdit
              }
            >
              <i className="fas fa-pen"></i>
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {message && (
        <div
          className={`owner-profile-message ${messageType}`}
        >
          <i
            className={
              messageType ===
              'success'
                ? 'fas fa-check-circle'
                : 'fas fa-exclamation-circle'
            }
          ></i>

          {message}
        </div>
      )}

      <section className="owner-profile-hero">
        <div className="owner-profile-identity">
          <div className="owner-profile-avatar-wrap">
            <div className="owner-profile-avatar">
              {!avatarFailed &&
              avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={
                    profile.name
                  }
                  onError={() =>
                    setAvatarFailed(
                      true
                    )
                  }
                />
              ) : (
                <span>
                  {profile.name
                    ?.charAt(0)
                    .toUpperCase() ||
                    'U'}
                </span>
              )}
            </div>

            <button
              type="button"
              className="owner-profile-camera"
              onClick={
                handleAvatarClick
              }
              disabled={
                avatarUploading
              }
              title="Change profile picture"
            >
              <i
                className={
                  avatarUploading
                    ? 'fas fa-spinner fa-spin'
                    : 'fas fa-camera'
                }
              ></i>
            </button>
          </div>

          <div className="owner-profile-main-info">
            <div className="owner-profile-name-row">
              <h2>
                {profile.name ||
                  'Ship Owner'}
              </h2>

              <span className="owner-profile-role">
                <i className="fas fa-anchor"></i>
                {isOwner
                  ? 'Ship Owner'
                  : profile.role}
              </span>

              <span
                className={`owner-profile-verification ${
                  profile.isVerified
                    ? 'verified'
                    : ''
                }`}
              >
                <i
                  className={
                    profile.isVerified
                      ? 'fas fa-check-circle'
                      : 'far fa-clock'
                  }
                ></i>

                {verificationText}
              </span>
            </div>

            <div className="owner-profile-company">
              <strong>
                {profile.company ||
                  'Company not provided'}
              </strong>

              {profile.position && (
                <span>
                  {profile.position}
                </span>
              )}
            </div>

            <div className="owner-profile-contact-line">
              {profile.email && (
                <span>
                  <i className="fas fa-envelope"></i>
                  {profile.email}
                </span>
              )}

              {profile.location && (
                <span>
                  <i className="fas fa-map-marker-alt"></i>
                  {
                    profile.location
                  }
                </span>
              )}

              {profile.website && (
                <a
                  href={getWebsiteHref(
                    profile.website
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-globe"></i>
                  {
                    profile.website
                  }
                </a>
              )}
            </div>
          </div>

          <div className="owner-profile-completeness">
            <div className="owner-profile-completeness-top">
              <span>
                Profile
              </span>

              <strong>
                {profileCompleteness}%
              </strong>
            </div>

            <div className="owner-profile-progress">
              <span
                style={{
                  width: `${profileCompleteness}%`
                }}
              ></span>
            </div>

            <small>
              Profile completeness
            </small>
          </div>
        </div>

        {isOwner && (
          <div className="owner-profile-stats">
            <div>
              <span className="owner-stat-icon">
                <i className="fas fa-ship"></i>
              </span>

              <span>
                <small>
                  Fleet
                </small>

                <strong>
                  {vessels.length}
                </strong>
              </span>
            </div>

            <div>
              <span className="owner-stat-icon">
                <i className="fas fa-circle-check"></i>
              </span>

              <span>
                <small>
                  Active Vessels
                </small>

                <strong>
                  {
                    activeVessels.length
                  }
                </strong>
              </span>
            </div>

            <div>
              <span className="owner-stat-icon">
                <i className="fas fa-screwdriver-wrench"></i>
              </span>

              <span>
                <small>
                  Maintenance
                </small>

                <strong>
                  {
                    maintenanceVessels.length
                  }
                </strong>
              </span>
            </div>

            <div>
              <span className="owner-stat-icon">
                <i className="fas fa-briefcase"></i>
              </span>

              <span>
                <small>
                  Open Jobs
                </small>

                <strong>
                  {
                    openRfqs.length
                  }
                </strong>
              </span>
            </div>

            <div>
              <span className="owner-stat-icon">
                <i className="fas fa-file-lines"></i>
              </span>

              <span>
                <small>
                  Total Requests
                </small>

                <strong>
                  {rfqs.length}
                </strong>
              </span>
            </div>
          </div>
        )}
      </section>

      {isEditing ? (
        <section className="owner-profile-card">
          <div className="owner-profile-card-head">
            <div>
              <i className="fas fa-pen"></i>

              <h3>
                Edit Profile
              </h3>
            </div>

            <span>
              Email and account
              status cannot be
              edited here
            </span>
          </div>

          <div className="owner-profile-edit-body">
            <div className="owner-profile-form-grid">
              <div className="owner-profile-field">
                <label>
                  Full Name
                </label>

                <input
                  type="text"
                  name="name"
                  value={
                    formData.name
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="owner-profile-field">
                <label>
                  Company Name
                </label>

                <input
                  type="text"
                  name="company"
                  value={
                    formData.company
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="owner-profile-field">
                <label>
                  Phone
                </label>

                <input
                  type="tel"
                  name="phone"
                  value={
                    formData.phone
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="owner-profile-field">
                <label>
                  Position
                </label>

                <input
                  type="text"
                  name="position"
                  value={
                    formData.position
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="owner-profile-field">
                <label>
                  Website
                </label>

                <input
                  type="text"
                  name="website"
                  value={
                    formData.website
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="https://company.com"
                />
              </div>

              <div className="owner-profile-field">
                <label>
                  Location
                </label>

                <input
                  type="text"
                  name="location"
                  value={
                    formData.location
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>
            </div>

            <div className="owner-profile-field owner-profile-bio-field">
              <label>
                Bio / Company
                Description
              </label>

              <textarea
                rows="3"
                name="bio"
                value={
                  formData.bio
                }
                onChange={
                  handleInputChange
                }
                placeholder="Brief information about you or your company"
              />
            </div>

            <div className="owner-profile-edit-actions">
              <button
                type="button"
                className="owner-profile-cancel-btn"
                onClick={
                  cancelEdit
                }
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="button"
                className="owner-profile-save-btn"
                onClick={
                  handleSave
                }
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="owner-profile-card">
            <div className="owner-profile-card-head">
              <div>
                <i className="fas fa-address-card"></i>

                <h3>
                  Personal & Company
                  Information
                </h3>
              </div>
            </div>

            <div className="owner-profile-info-grid">
              <div>
                <small>
                  Full Name
                </small>
                <strong>
                  {profile.name ||
                    'Not provided'}
                </strong>
              </div>

              <div>
                <small>
                  Email Address
                </small>
                <strong>
                  {profile.email ||
                    'Not provided'}
                </strong>
              </div>

              <div>
                <small>
                  Phone
                </small>
                <strong>
                  {profile.phone ||
                    'Not provided'}
                </strong>
              </div>

              <div>
                <small>
                  Company
                </small>
                <strong>
                  {profile.company ||
                    'Not provided'}
                </strong>
              </div>

              <div>
                <small>
                  Position
                </small>
                <strong>
                  {profile.position ||
                    'Not provided'}
                </strong>
              </div>

              <div>
                <small>
                  Location
                </small>
                <strong>
                  {profile.location ||
                    'Not provided'}
                </strong>
              </div>

              <div>
                <small>
                  Website
                </small>

                {profile.website ? (
                  <a
                    href={getWebsiteHref(
                      profile.website
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {profile.website}
                  </a>
                ) : (
                  <strong>
                    Not provided
                  </strong>
                )}
              </div>

              <div>
                <small>
                  Registration No.
                </small>
                <strong>
                  {profile.registrationNo ||
                    'Not provided'}
                </strong>
              </div>

              {isOwner && (
                <>
                  <div>
                    <small>
                      Years in Operation
                    </small>
                    <strong>
                      {profile.yearsInOperation ||
                        'Not provided'}
                    </strong>
                  </div>

                  <div>
                    <small>
                      Registered Fleet Size
                    </small>
                    <strong>
                      {profile.fleetSize ||
                        vessels.length ||
                        'Not provided'}
                    </strong>
                  </div>

                  <div>
                    <small>
                      Actual Fleet Size
                    </small>
                    <strong>
                      {
                        vessels.length
                      }
                    </strong>
                  </div>

                  <div>
                    <small>
                      Total Fleet GRT
                    </small>
                    <strong>
                      {formatNumber(
                        totalGRT
                      )}
                    </strong>
                  </div>

                  <div className="owner-profile-wide-info">
                    <small>
                      Vessel Types
                    </small>
                    <strong>
                      {vesselTypes.length
                        ? vesselTypes.join(
                            ', '
                          )
                        : Array.isArray(
                              profile.vesselTypes
                            ) &&
                            profile
                              .vesselTypes
                              .length
                          ? profile.vesselTypes.join(
                              ', '
                            )
                          : 'Not provided'}
                    </strong>
                  </div>

                  <div className="owner-profile-wide-info">
                    <small>
                      Fleet Flags
                    </small>
                    <strong>
                      {flags.length
                        ? flags.join(
                            ', '
                          )
                        : 'Not available'}
                    </strong>
                  </div>
                </>
              )}
            </div>

            <div className="owner-profile-about">
              <span>
                <i className="fas fa-align-left"></i>
                Bio
              </span>

              <p>
                {profile.bio ||
                  profile.description ||
                  'No bio or company description has been added yet.'}
              </p>
            </div>
          </section>

          <section className="owner-profile-card">
            <div className="owner-profile-card-head">
              <div>
                <i className="fas fa-shield-alt"></i>

                <h3>
                  Account Information
                </h3>
              </div>

              <button
                type="button"
                className="owner-profile-security-link"
                onClick={() =>
                  setShowSecurity(
                    !showSecurity
                  )
                }
              >
                <i className="fas fa-lock"></i>

                {showSecurity
                  ? 'Close Security'
                  : 'Security'}
              </button>
            </div>

            <div className="owner-profile-account-grid">
              <div>
                <small>
                  Account Role
                </small>

                <strong>
                  {isOwner
                    ? 'Ship Owner'
                    : profile.role ||
                      'User'}
                </strong>
              </div>

              <div>
                <small>
                  Account Status
                </small>

                <strong
                  className={
                    profile.isActive ===
                    false
                      ? 'owner-status-inactive'
                      : 'owner-status-active'
                  }
                >
                  <i className="fas fa-circle"></i>
                  {accountStatus}
                </strong>
              </div>

              <div>
                <small>
                  Verification
                </small>

                <strong>
                  {verificationText}
                </strong>
              </div>

              <div>
                <small>
                  Sign-in Method
                </small>

                <strong>
                  {authMethod}
                </strong>
              </div>

              <div>
                <small>
                  Profile Status
                </small>

                <strong>
                  {profile.profileCompleted
                    ? 'Complete'
                    : 'Incomplete'}
                </strong>
              </div>

              <div>
                <small>
                  Member Since
                </small>

                <strong>
                  {formatDate(
                    profile.createdAt
                  )}
                </strong>
              </div>

              <div>
                <small>
                  Last Login
                </small>

                <strong>
                  {formatDateTime(
                    profile.lastLogin
                  )}
                </strong>
              </div>

              <div>
                <small>
                  Last Updated
                </small>

                <strong>
                  {formatDateTime(
                    profile.updatedAt
                  )}
                </strong>
              </div>
            </div>

            {showSecurity && (
              <div className="owner-profile-security-panel">
                {profile.authProvider ===
                  'google' ||
                profile.isGoogleLogin ? (
                  <div className="owner-profile-google-security">
                    <i className="fab fa-google"></i>

                    <div>
                      <strong>
                        Google Account
                      </strong>

                      <p>
                        This account uses
                        Google authentication.
                        Password security is
                        managed through your
                        Google account.
                      </p>
                    </div>
                  </div>
                ) : (
                  <form
                    onSubmit={
                      handlePasswordChange
                    }
                  >
                    <div className="owner-profile-password-grid">
                      <div className="owner-profile-field">
                        <label>
                          Current Password
                        </label>

                        <input
                          type="password"
                          value={
                            passwordData.current
                          }
                          onChange={(
                            event
                          ) =>
                            setPasswordData(
                              (
                                previous
                              ) => ({
                                ...previous,
                                current:
                                  event
                                    .target
                                    .value
                              })
                            )
                          }
                          required
                        />
                      </div>

                      <div className="owner-profile-field">
                        <label>
                          New Password
                        </label>

                        <input
                          type="password"
                          value={
                            passwordData.new
                          }
                          onChange={(
                            event
                          ) =>
                            setPasswordData(
                              (
                                previous
                              ) => ({
                                ...previous,
                                new:
                                  event
                                    .target
                                    .value
                              })
                            )
                          }
                          required
                        />
                      </div>

                      <div className="owner-profile-field">
                        <label>
                          Confirm Password
                        </label>

                        <input
                          type="password"
                          value={
                            passwordData.confirm
                          }
                          onChange={(
                            event
                          ) =>
                            setPasswordData(
                              (
                                previous
                              ) => ({
                                ...previous,
                                confirm:
                                  event
                                    .target
                                    .value
                              })
                            )
                          }
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        className="owner-profile-password-btn"
                        disabled={
                          changingPassword
                        }
                      >
                        {changingPassword ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Updating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-key"></i>
                            Update Password
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </section>

          {isOwner && (
            <section className="owner-profile-card">
              <div className="owner-profile-card-head">
                <div>
                  <i className="fas fa-ship"></i>

                  <h3>
                    Fleet Overview
                  </h3>
                </div>

                <button
                  type="button"
                  className="owner-profile-card-action"
                  onClick={() =>
                    navigate(
                      '/owner/my-ships'
                    )
                  }
                >
                  Manage Fleet
                  <i className="fas fa-arrow-right"></i>
                </button>
              </div>

              {recentVessels.length >
              0 ? (
                <div className="owner-fleet-list">
                  {recentVessels.map(
                    (vessel) => (
                      <button
                        type="button"
                        key={
                          vessel._id
                        }
                        className="owner-fleet-row"
                        onClick={() =>
                          navigate(
                            `/owner/ship/${vessel._id}`
                          )
                        }
                      >
                        <span className="owner-fleet-icon">
                          <i className="fas fa-ship"></i>
                        </span>

                        <span className="owner-fleet-main">
                          <strong>
                            {
                              vessel.name
                            }
                          </strong>

                          <small>
                            IMO{' '}
                            {vessel.imo ||
                              'N/A'}
                          </small>
                        </span>

                        <span className="owner-fleet-detail">
                          <small>
                            Type
                          </small>

                          <strong>
                            {vessel.type ||
                              'N/A'}
                          </strong>
                        </span>

                        <span className="owner-fleet-detail">
                          <small>
                            Flag
                          </small>

                          <strong>
                            {vessel.flag ||
                              'N/A'}
                          </strong>
                        </span>

                        <span className="owner-fleet-detail">
                          <small>
                            Route
                          </small>

                          <strong>
                            {vessel.lastPort ||
                              '—'}
                            {' → '}
                            {vessel.nextPort ||
                              '—'}
                          </strong>
                        </span>

                        <span className="owner-fleet-detail">
                          <small>
                            Health
                          </small>

                          <strong>
                            {vessel.health ??
                              100}
                            %
                          </strong>
                        </span>

                        <span
                          className={`owner-vessel-status ${
                            vessel.status ||
                            'inactive'
                          }`}
                        >
                          {vessel.status ||
                            'inactive'}
                        </span>

                        <i className="fas fa-chevron-right owner-fleet-arrow"></i>
                      </button>
                    )
                  )}
                </div>
              ) : (
                <div className="owner-profile-empty">
                  <i className="fas fa-ship"></i>

                  <div>
                    <strong>
                      No vessels added
                    </strong>

                    <span>
                      Add your first
                      vessel to start
                      managing your
                      fleet.
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        '/owner/my-ships'
                      )
                    }
                  >
                    Manage Fleet
                  </button>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default Profile;